import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import { Button, ErrorState, Panel } from '../ui';

/**
 * Admin → Avatar Management.
 *
 * This screen exists for one sentence in the brief: **do not require a code
 * deployment to disable a problematic asset.** The artwork comes from outside
 * the project, so the realistic emergency is not a bug — it is somebody writing
 * to say a sprite should not be up. That has to be one click, at three in the
 * morning, by somebody who cannot deploy.
 *
 * So the layout puts the switch first and everything else behind an expander.
 * A table where "disable" is the ninth column of a row you have to scroll
 * sideways to reach is a table that fails the only test that matters.
 *
 * Nothing here can edit `assetUrl` or `filename`, and that is deliberate rather
 * than an omission: a free-text asset URL in an admin form would let anybody
 * holding this permission point every profile in the game at an arbitrary
 * remote host. Where a sprite comes from is decided by the reviewed import.
 */

interface AdminAvatar {
  id: string;
  slug: string;
  displayName: string;
  filename: string;
  assetUrl: string;
  sourceUrl: string;
  generation: number;
  trainerClass: string;
  category: string;
  presentation: string;
  artistCredit: string | null;
  permissionStatus: string;
  editingAllowed: boolean;
  localCopy: boolean;
  enabled: boolean;
  playerSelectable: boolean;
  npcOnly: boolean;
  unlockType: string;
  unlockArg: string | null;
  unlockValue: number | null;
  unlockNote: string | null;
  sortOrder: number;
  _count: { profiles: number; favorites: number; grants: number };
}

interface Payload {
  avatars: AdminAvatar[];
  summary: { total: number; enabled: number; unverified: number; awaitingCopy: number };
}

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'reserved', label: 'NPC only' },
];

export function AvatarAdminTab() {
  const { push } = useToast();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const state = useAsync<Payload>(
    () => api.get(`/admin/avatars?status=${status}&search=${encodeURIComponent(search)}`),
    [status, search],
  );

  async function patch(avatar: AdminAvatar, data: Partial<AdminAvatar>) {
    try {
      await api.patch(`/admin/avatars/${avatar.id}`, data);
      push('success', `${avatar.displayName} updated.`);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save.');
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 260 }} />;

  const { avatars, summary } = state.data;

  return (
    <div className="stack">
      <Panel title="Trainer Avatars" hint={`${summary.enabled} of ${summary.total} enabled`}>
        <div className="stack">
          <p className="tiny muted" style={{ margin: 0 }}>
            Disabling an avatar removes it from the picker immediately and falls every
            profile wearing it back to their previous avatar. Nothing here needs a
            deployment. An avatar whose permission status is unverified cannot be enabled
            at all — resolve the status first.
          </p>

          {summary.unverified > 0 && (
            <p className="tiny" style={{ margin: 0, color: 'var(--berry)' }}>
              {summary.unverified} avatar(s) have an unresolved permission status and are
              not being shown to players.
            </p>
          )}
          {summary.awaitingCopy > 0 && (
            <p className="tiny muted" style={{ margin: 0 }}>
              {summary.awaitingCopy} avatar(s) have no local copy yet. Run{' '}
              <code>npm run import:avatars -w server</code> to review and download them.
            </p>
          )}

          <div className="row row--wrap" style={{ gap: 'var(--gap-xs)' }}>
            {STATUSES.map((entry) => (
              <button
                key={entry.value}
                type="button"
                className={`chip ${status === entry.value ? 'chip--on' : ''}`}
                onClick={() => setStatus(entry.value)}
              >
                {entry.label}
              </button>
            ))}
            <input
              className="input"
              type="search"
              value={search}
              placeholder="Search name, slug or file…"
              onChange={(event) => setSearch(event.target.value)}
              style={{ flex: '1 1 200px' }}
            />
          </div>

          <div className="avadmin__list">
            {avatars.map((avatar) => (
              <div key={avatar.id}>
                <div
                  className={[
                    'avadmin__row',
                    avatar.enabled ? '' : 'avadmin__row--off',
                    avatar.permissionStatus === 'UNVERIFIED' ? 'avadmin__row--flag' : '',
                  ].join(' ')}
                >
                  <span className="tavatar">
                    <img
                      className="pixel tavatar__img"
                      src={avatar.assetUrl}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      decoding="async"
                    />
                  </span>

                  <div className="stack-sm" style={{ minWidth: 0 }}>
                    <strong className="tiny">{avatar.displayName}</strong>
                    <span className="tiny muted mono">
                      {avatar.slug} · {avatar.filename}
                    </span>
                    <span className="tiny muted">
                      {avatar.permissionStatus.replace('_', ' ').toLowerCase()}
                      {avatar.artistCredit ? ` · by ${avatar.artistCredit}` : ''}
                      {avatar.npcOnly ? ' · NPC only' : ''}
                      {avatar._count.profiles > 0 ? ` · worn by ${avatar._count.profiles}` : ''}
                    </span>
                  </div>

                  <div className="avadmin__actions">
                    <Button
                      variant={avatar.enabled ? 'ghost' : 'gold'}
                      onClick={() => void patch(avatar, { enabled: !avatar.enabled })}
                    >
                      {avatar.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setOpen(open === avatar.id ? null : avatar.id)}
                    >
                      {open === avatar.id ? 'Close' : 'Edit'}
                    </Button>
                  </div>
                </div>

                {open === avatar.id && <Editor avatar={avatar} onSave={patch} />}
              </div>
            ))}
          </div>

          {avatars.length === 0 && (
            <p className="tiny muted" style={{ margin: 0 }}>
              No avatars match that.
            </p>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * The full editor for one avatar.
 *
 * A local draft with an explicit Save, rather than a field-by-field autosave.
 * Two of these fields interact — marking something CREDITED_ARTIST needs an
 * artist in the same submission — and a per-field save would reject the first
 * half of a valid change.
 */
function Editor({
  avatar,
  onSave,
}: {
  avatar: AdminAvatar;
  onSave: (avatar: AdminAvatar, data: Partial<AdminAvatar>) => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    displayName: avatar.displayName,
    generation: avatar.generation,
    trainerClass: avatar.trainerClass,
    category: avatar.category,
    presentation: avatar.presentation,
    artistCredit: avatar.artistCredit ?? '',
    permissionStatus: avatar.permissionStatus,
    playerSelectable: avatar.playerSelectable,
    npcOnly: avatar.npcOnly,
    unlockType: avatar.unlockType,
    unlockArg: avatar.unlockArg ?? '',
    unlockValue: avatar.unlockValue ?? 0,
    unlockNote: avatar.unlockNote ?? '',
    sortOrder: avatar.sortOrder,
  });
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft({ ...draft, [key]: value });

  return (
    <div className="pxwell stack" style={{ padding: 'var(--gap-sm)' }}>
      <div className="avadmin__editor">
        <Field label="Display name">
          <input
            className="input"
            value={draft.displayName}
            onChange={(event) => set('displayName', event.target.value)}
          />
        </Field>
        <Field label="Trainer class">
          <input
            className="input"
            value={draft.trainerClass}
            onChange={(event) => set('trainerClass', event.target.value)}
          />
        </Field>
        <Field label="Generation">
          <input
            className="input"
            type="number"
            min={1}
            max={9}
            value={draft.generation}
            onChange={(event) => set('generation', Number(event.target.value))}
          />
        </Field>
        <Field label="Category">
          <select
            className="input"
            value={draft.category}
            onChange={(event) => set('category', event.target.value)}
          >
            {[
              'TRAINER_CLASS',
              'GYM_LEADER',
              'ELITE_FOUR',
              'CHAMPION',
              'RIVAL',
              'PROFESSOR',
              'VILLAIN',
              'SPECIAL',
            ].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Presentation">
          <select
            className="input"
            value={draft.presentation}
            onChange={(event) => set('presentation', event.target.value)}
          >
            {['NEUTRAL', 'MALE', 'FEMALE'].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sort order">
          <input
            className="input"
            type="number"
            value={draft.sortOrder}
            onChange={(event) => set('sortOrder', Number(event.target.value))}
          />
        </Field>

        <Field label="Artist credit" hint="Never remove one that is already recorded.">
          <input
            className="input"
            value={draft.artistCredit}
            placeholder="Nobody credited at source"
            onChange={(event) => set('artistCredit', event.target.value)}
          />
        </Field>
        <Field label="Permission status">
          <select
            className="input"
            value={draft.permissionStatus}
            onChange={(event) => set('permissionStatus', event.target.value)}
          >
            {['OFFICIAL_SOURCE', 'CREDITED_ARTIST', 'UNVERIFIED'].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Unlock">
          <select
            className="input"
            value={draft.unlockType}
            onChange={(event) => set('unlockType', event.target.value)}
          >
            {[
              'NONE',
              'GRANT',
              'LEVEL',
              'BADGE',
              'COLLECTION',
              'DEX',
              'SHINY_DEX',
              'EVENT',
              'WAR_SEASON',
              'ACHIEVEMENT',
            ].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Unlock key" hint="Badge key, wing key, event key…">
          <input
            className="input"
            value={draft.unlockArg}
            onChange={(event) => set('unlockArg', event.target.value)}
          />
        </Field>
        <Field label="Unlock value">
          <input
            className="input"
            type="number"
            min={0}
            value={draft.unlockValue}
            onChange={(event) => set('unlockValue', Number(event.target.value))}
          />
        </Field>
        <Field label="Requirement text" hint="How it reads to somebody who hasn't earned it.">
          <input
            className="input"
            value={draft.unlockNote}
            onChange={(event) => set('unlockNote', event.target.value)}
          />
        </Field>
      </div>

      <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
        <label className="tiny row" style={{ gap: 'var(--gap-xs)' }}>
          <input
            type="checkbox"
            checked={draft.playerSelectable}
            onChange={(event) => set('playerSelectable', event.target.checked)}
          />
          Player selectable
        </label>
        <label className="tiny row" style={{ gap: 'var(--gap-xs)' }}>
          <input
            type="checkbox"
            checked={draft.npcOnly}
            onChange={(event) => set('npcOnly', event.target.checked)}
          />
          Reserved for Living Trainers
        </label>
      </div>

      <p className="tiny muted" style={{ margin: 0 }}>
        Source:{' '}
        <a href={avatar.sourceUrl} target="_blank" rel="noreferrer noopener">
          {avatar.filename}
        </a>
        {avatar.localCopy ? ' · local copy held' : ' · no local copy yet'}
        {avatar.editingAllowed ? '' : ' · editing not permitted'}
      </p>

      <div className="row">
        <Button
          variant="gold"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            await onSave(avatar, {
              ...draft,
              artistCredit: draft.artistCredit.trim() || null,
              unlockArg: draft.unlockArg.trim() || null,
              unlockNote: draft.unlockNote.trim() || null,
              unlockValue: draft.unlockValue || null,
            } as Partial<AdminAvatar>);
            setBusy(false);
          }}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="stack-sm">
      <span className="tiny mono muted">{label.toUpperCase()}</span>
      {children}
      {hint && <span className="tiny muted">{hint}</span>}
    </label>
  );
}
